import { Transform, Type } from "class-transformer";
import { BaseEntityDto } from "@/core/common/entities/base-entity.dto";
import {
  PurchaseOrderProjectDestinationEnum,
  PurchaseOrderStatusEnum,
  canArchivePurchaseOrder,
} from "@altertec_gparn/lib";
import { SupplierDto } from "@/core/features/suppliers/supplier.dto";
import { parseApiDate } from "@/utils/dates/dateFormats";
import { toEuros } from "@/utils/numberUtils";
import { PurchaseOrderLineDto } from "@/core/features/purchase-orders/purchase-order-line.dto";
import { CompanyDto } from "@/core/features/companies/company.dto";
import { UserDto } from "@/core/features/users/user.dto";
import { OperativeUnitDto } from "@/core/features/operative-units/operative-unit.dto";
import { ProjectDto } from "@/core/features/projects/project.dto";
import { FileDto } from "@/core/features/file/file.dto";
import { WarehouseDto } from "@/core/features/warehouses/warehouse.dto";
import { ExpenseSummaryDto } from "@/core/features/expense/expense-summary.dto";

export class PurchaseOrderDto extends BaseEntityDto {
  code: string;

  status?: PurchaseOrderStatusEnum;

  @Type(() => SupplierDto)
  supplier: SupplierDto;
  supplierId: string;

  @Type(() => CompanyDto)
  company?: CompanyDto;
  companyId: string;

  @Type(() => OperativeUnitDto)
  operativeUnit: OperativeUnitDto;
  operativeUnitId: string;

  @Type(() => WarehouseDto)
  warehouse: WarehouseDto;
  warehouseId: string;

  @Type(() => ProjectDto)
  project?: ProjectDto;
  projectId?: string;
  projectDestination?: PurchaseOrderProjectDestinationEnum;

  addressName: string;
  address: string;
  addressPostalCode: string;
  addressCity: string;
  addressProvince: string;
  addressCountry: string;

  @Transform(({ value }) => parseApiDate(value))
  purchasedAt?: Date;

  @Transform(({ value }) => toEuros(value))
  totalAmount: number;

  @Type(() => PurchaseOrderLineDto)
  lines?: PurchaseOrderLineDto[];

  @Type(() => ExpenseSummaryDto)
  expense?: ExpenseSummaryDto;

  @Type(() => UserDto)
  createdBy: UserDto;

  @Type(() => FileDto)
  files: FileDto[];

  @Type(() => UserDto)
  approvedBy?: UserDto;
  approvedById?: string;

  @Transform(({ value }) => parseApiDate(value))
  approvedAt?: Date;

  @Transform(({ value }) => new Date(value))
  date: Date;

  archived: boolean;

  sapDocNum?: number | null;
  sapDocSeriesName?: string | null;
  sapSyncError?: string | null;

  isEditable() {
    return [PurchaseOrderStatusEnum.DRAFT, PurchaseOrderStatusEnum.PRICED].includes(this.status);
  }

  isArchivable(): boolean {
    return canArchivePurchaseOrder(this.status);
  }

  isCancelled(): boolean {
    return this.status === PurchaseOrderStatusEnum.CANCELLED;
  }

  toString(): string {
    const supplier = this.supplier ? `- ${this.supplier?.toString()}` : "";
    return `${this.code} ${supplier}`;
  }

  getAddress() {
    const parts = [
      this.addressName,
      this.address,
      this.addressPostalCode,
      this.addressCity,
      this.addressProvince,
      this.addressCountry,
    ].filter(Boolean);
    return parts.join(", ");
  }
}
